import { useState, useEffect, useRef } from 'react'
import { MessageCircle, X, Send, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { toast } from 'sonner'
import authService from '@/lib/auth'

interface Message {
  id: number
  role: 'user' | 'assistant'
  content: string
  created_at: string
}

interface Conversation {
  id: number
  title: string
  message_count: number
}

interface ChatBubbleProps {
  portalType: 'clinician' | 'patient'
  apiBasePath: string
  tokenKey: 'auth_token' | 'patient_session_token'
}

export function ChatBubble({ portalType, apiBasePath, tokenKey }: ChatBubbleProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const getAuthHeaders = () => {
    // For clinician portal, use authService for consistent auth handling
    if (tokenKey === 'auth_token') {
      return {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        ...authService.authHeaders()
      }
    }
    // For patient portal, use the patient session token
    const token = localStorage.getItem(tokenKey)
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (isOpen && !currentConversation) {
      loadConversations()
    }
  }, [isOpen])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const loadConversations = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${apiBasePath}/conversations`, {
        headers: getAuthHeaders()
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('AI Chat: Failed to load conversations', { status: response.status, error: errorText })
        throw new Error(`Failed to load conversations (${response.status})`)
      }

      const data = await response.json()
      setConversations(data.conversations || [])

      // Auto-select first conversation or create new one
      if (data.conversations?.length > 0) {
        setCurrentConversation(data.conversations[0])
        // Load messages for the first conversation
        await loadMessages(data.conversations[0].id)
      } else {
        await createNewConversation()
      }
    } catch (error: any) {
      console.error('AI Chat: Error loading conversations', error)
      toast.error(error.message || 'Failed to load conversations')
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (conversationId: number) => {
    try {
      const response = await fetch(`${apiBasePath}/conversations/${conversationId}/messages`, {
        headers: getAuthHeaders()
      })

      if (response.ok) {
        const data = await response.json()
        setMessages(data.messages || [])
      }
    } catch (error) {
      console.error('AI Chat: Error loading messages', error)
    }
  }

  const createNewConversation = async () => {
    try {
      const response = await fetch(`${apiBasePath}/conversations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: 'New Conversation' })
      })

      if (!response.ok) {
        throw new Error('Failed to create conversation')
      }

      const data = await response.json()
      if (data.success) {
        setCurrentConversation(data.conversation)
        setConversations([data.conversation, ...conversations])
        setMessages([])
      }
    } catch (error) {
      toast.error('Failed to create conversation')
    }
  }

  const sendMessage = async () => {
    if (!inputMessage.trim() || !currentConversation) return

    const userMessage = inputMessage
    setInputMessage('')
    setSending(true)

    // Add user message optimistically
    const tempUserMsg: Message = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    }
    setMessages([...messages, tempUserMsg])

    try {
      const response = await fetch(`${apiBasePath}/conversations/${currentConversation.id}/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ content: userMessage })
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      const data = await response.json()

      if (data.success) {
        // Replace temp message with actual messages from server
        setMessages(prev => [
          ...prev.filter(m => m.id !== tempUserMsg.id),
          data.user_message,
          data.assistant_message
        ])
      } else {
        toast.error(data.errors?.[0] || 'Failed to send message')
        setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id))
      }
    } catch (error) {
      toast.error('Failed to send message')
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id))
    } finally {
      setSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <>
      {/* Chat Bubble Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {!isOpen && (
          <Button
            onClick={() => setIsOpen(true)}
            size="lg"
            className="h-14 w-14 rounded-full shadow-lg"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        )}
      </div>

      {/* Chat Interface */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 shadow-xl">
          <Card className="flex h-[600px] flex-col">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-lg">AI Assistant</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="flex flex-1 flex-col gap-4 p-4 overflow-hidden">
              {loading ? (
                <div className="flex flex-1 items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <ScrollArea className="flex-1 h-0 pr-4">
                    <div className="space-y-4">
                      {messages.length === 0 ? (
                        <div className="flex min-h-[300px] items-center justify-center text-center">
                          <p className="text-muted-foreground text-sm">
                            Start a conversation by typing a message below.
                          </p>
                        </div>
                      ) : (
                        messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                message.role === 'user'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Input */}
                  <div className="flex items-end gap-2">
                    <Input
                      placeholder="Type your message..."
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={sending || !currentConversation}
                      className="flex-1"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!inputMessage.trim() || sending || !currentConversation}
                      size="icon"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}
